/**
 * Save-time out-of-service (OOS) guard for tractor assignments.
 *
 * The tractor pickers already disable trucks whose active service window
 * overlaps the order's window (`computeOosBlockedIdsForWindow`), but a truck
 * can go out of service *after* it was picked — e.g. the form was left open,
 * or AAM sent a service event while the operator was editing. Without a check
 * at submit time the stale selection saves happily.
 *
 * This module is the pure rule shared by Create Order and Edit Order so the
 * picker and the save path can only ever agree: both consume the same
 * per-leg blocked-id sets.
 */
import type { OrderFormData } from "@/components/orders/orderFormTypes";
import type { Tractor } from "@/types/tms";
import { resolveFormLegs } from "@/lib/order-form-legs";

export interface OosAssignmentCheck {
  tractorId: string;
  tractorLabel: string | null;
  /** Index into the per-leg window/blocked-set arrays. */
  legIndex: number;
  /** Human context for the message, e.g. "Stop 2". Null for order-level. */
  context: string | null;
}

/**
 * Flattens a form into the tractor assignments the OOS rule must approve:
 * one per leg for multi-leg orders (so a shared truck is validated against
 * every leg's own window), otherwise a single order-level assignment.
 */
export function buildOosAssignmentChecks(
  formData: OrderFormData,
  tractors: Tractor[] | undefined,
): OosAssignmentCheck[] {
  const nameById = new Map((tractors ?? []).map((t) => [t.id, t.name]));
  const legs = resolveFormLegs(formData);
  const multi = legs.length > 1;

  return legs
    .filter((leg) => !!leg.tractorId)
    .map((leg) => ({
      tractorId: leg.tractorId,
      tractorLabel: nameById.get(leg.tractorId) ?? null,
      legIndex: leg.index,
      context: multi ? `Stop ${leg.index + 1}` : null,
    }));
}

/**
 * Returns an error message when any assigned tractor is out of service for
 * its own leg's window, else null.
 *
 * `blockedIdsByLeg` comes from `computeOosBlockedIdsForWindow`, the same
 * helper the picker uses.
 */
export function validateTractorOosAssignments(
  checks: readonly OosAssignmentCheck[],
  blockedIdsByLeg: readonly ReadonlySet<string>[],
): string | null {
  for (const check of checks) {
    const blocked = blockedIdsByLeg[check.legIndex];
    if (!blocked?.has(check.tractorId)) continue;
    const truck = check.tractorLabel ?? "The selected truck";
    const where = check.context ? ` on ${check.context}` : "";
    return `${truck} is out of service for the scheduled window${where}. Pick another truck before saving.`;
  }
  return null;
}
